/**
 * HospitalQuickEditDialog
 * -----------------------
 * Popup form to view/edit the hospital record linked to a CRM account
 * without leaving the CRM dashboard. Surfaces the same essentials as the
 * Hospitals tab editor (Primary contact, allowed email domains, billing,
 * notes) plus an inline "Add department contact" widget so admins can
 * drop new contacts in mid-call.
 *
 * Resolves the hospital by:
 *   1. account.hospital_id (preferred)
 *   2. case-insensitive name match against /health-systems
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Loader2, Save, ExternalLink, Mail, Phone, Hospital, UserPlus, X, Building2,
} from "lucide-react";
import HospitalOrderRecipients from "./HospitalOrderRecipients";

const API = process.env.REACT_APP_BACKEND_URL;

const DEPARTMENTS = [
  "Supply Chain", "Facilities Management", "Environmental Services (EVS)",
  "Biomedical Engineering (Bio Med)", "Sterile Processing (SPD)", "Nursing",
  "Administration", "Finance / Billing", "IT / Informatics", "Infection Prevention",
  "Operations", "Risk / Compliance", "Executive Leadership", "Other",
];

export default function HospitalQuickEditDialog({
  open, onOpenChange, account, token, onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hospital, setHospital] = useState(null);
  const [form, setForm] = useState({
    name: "", short_name: "",
    allowed_email_domains: "",
    primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "",
    billing_address: "", billing_city: "", billing_state: "", billing_zip: "",
    notes: "",
  });
  // Tiny inline single-contact add (mirrors AddSingleContactDialog essentials)
  const [newContact, setNewContact] = useState({
    name: "", email: "", title: "", phone: "", department: "Supply Chain",
  });
  const [addingContact, setAddingContact] = useState(false);

  // Resolve hospital_id from the account
  useEffect(() => {
    if (!open || !account) return;
    setLoading(true);
    (async () => {
      try {
        let hsId = account.hospital_id;
        // Fallback: try by name
        if (!hsId && account.name) {
          const r = await axios.get(`${API}/api/health-systems`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const arr = r.data?.health_systems || [];
          const match = arr.find(
            (h) => (h.name || "").toLowerCase() === (account.name || "").toLowerCase()
          );
          if (match) hsId = match.id;
        }
        if (!hsId) {
          toast.error("This CRM account isn't linked to a hospital yet — add one from the Hospitals tab first");
          setHospital(null);
          setLoading(false);
          return;
        }
        const r2 = await axios.get(`${API}/api/admin/health-systems/${hsId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const hs = r2.data?.health_system || r2.data || {};
        setHospital(hs);
        setForm({
          name: hs.name || "",
          short_name: hs.short_name || "",
          allowed_email_domains: (hs.allowed_email_domains || []).join(", "),
          primary_contact_name: hs.primary_contact_name || "",
          primary_contact_email: hs.primary_contact_email || "",
          primary_contact_phone: hs.primary_contact_phone || "",
          billing_address: hs.billing_address || "",
          billing_city: hs.billing_city || "",
          billing_state: hs.billing_state || "",
          billing_zip: hs.billing_zip || "",
          notes: hs.notes || "",
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load hospital");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, account, token]);

  const save = async () => {
    if (!hospital?.id) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/admin/health-systems/${hospital.id}`,
        {
          name: form.name,
          short_name: form.short_name || null,
          allowed_email_domains: form.allowed_email_domains
            ? form.allowed_email_domains.split(",").map((s) => s.trim()).filter(Boolean)
            : [],
          primary_contact_name: form.primary_contact_name || null,
          primary_contact_email: form.primary_contact_email || null,
          primary_contact_phone: form.primary_contact_phone || null,
          billing_address: form.billing_address || null,
          billing_city: form.billing_city || null,
          billing_state: form.billing_state || null,
          billing_zip: form.billing_zip || null,
          notes: form.notes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Saved ${form.name}`);
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = "Save failed";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d.msg || "").join("; ");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const addContact = async () => {
    if (!hospital?.id) return;
    if (!newContact.email?.trim()) { toast.error("Email is required"); return; }
    setAddingContact(true);
    try {
      const r = await axios.post(
        `${API}/api/health-systems/${hospital.id}/contacts`,
        {
          name: newContact.name?.trim() || null,
          email: newContact.email.trim().toLowerCase(),
          phone: newContact.phone || null,
          title: newContact.title || null,
          department: newContact.department || null,
          role: "staff",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const created = r.data?.contact;
      toast.success(`Added ${created?.name || newContact.email} → ${hospital.name}`);
      setNewContact({ name: "", email: "", title: "", phone: "", department: "Supply Chain" });
      // Refresh hospital so the contact list re-renders
      const r2 = await axios.get(`${API}/api/admin/health-systems/${hospital.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHospital(r2.data?.health_system || r2.data || hospital);
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = "Add failed";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d.msg || "").join("; ");
      toast.error(msg);
    } finally {
      setAddingContact(false);
    }
  };

  const openFullPage = () => {
    if (!hospital?.id) return;
    // Navigate to the dedicated Hospitals tab — auto-opens detail via `?hs=<id>`
    window.location.href = `/admin/hospitals?hs=${hospital.id}`;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving && !addingContact) onOpenChange(o); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hospital-quick-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hospital className="w-5 h-5 text-rose-600" />
            Manage hospital{hospital ? `: ${hospital.name}` : ""}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-16 flex items-center justify-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading hospital…
          </div>
        )}

        {!loading && !hospital && (
          <div className="py-10 text-center text-sm text-slate-500">
            <Building2 className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p>This CRM account isn't linked to a hospital record yet.</p>
            <p className="mt-1">Create one from the <strong>Hospitals</strong> tab, then return here.</p>
          </div>
        )}

        {!loading && hospital && (
          <div className="space-y-5">
            {/* Identity */}
            <section className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Identity</h4>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="hqe-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Short name</Label>
                  <Input
                    value={form.short_name}
                    onChange={(e) => setForm({ ...form, short_name: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Allowed email domains <span className="text-slate-400 font-normal">(comma-separated)</span></Label>
                <Input
                  value={form.allowed_email_domains}
                  onChange={(e) => setForm({ ...form, allowed_email_domains: e.target.value })}
                  placeholder="harrishealth.org, hhs.org"
                  data-testid="hqe-domains"
                />
              </div>
            </section>

            {/* Primary contact */}
            <section className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Primary contact</h4>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Name"
                  value={form.primary_contact_name}
                  onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })}
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={form.primary_contact_email}
                  onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })}
                />
              </div>
              <Input
                placeholder="Phone"
                value={form.primary_contact_phone}
                onChange={(e) => setForm({ ...form, primary_contact_phone: e.target.value })}
              />
            </section>

            {/* Permanent order-notification recipients */}
            <HospitalOrderRecipients
              healthSystemId={hospital.id}
              token={token}
              suggestions={(hospital.contacts || []).filter((c) => c.email)}
            />

            {/* Existing department contacts (read-only quick view) */}
            <section className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                Department contacts
                <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                  {(hospital.contacts || []).length}
                </Badge>
              </h4>
              {(hospital.contacts || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic">No contacts yet — add the first one below.</p>
              ) : (
                <div className="max-h-44 overflow-y-auto border border-slate-200 rounded divide-y">
                  {(hospital.contacts || []).map((c, i) => (
                    <div key={c.id || i} className="px-2 py-1.5 flex items-center gap-2 text-xs">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{c.name || c.email}</div>
                        <div className="text-slate-500 truncate flex items-center gap-2">
                          <Mail className="w-3 h-3" /> {c.email}
                          {c.phone && (<><Phone className="w-3 h-3 ml-1" /> {c.phone}</>)}
                        </div>
                      </div>
                      {c.title && <span className="text-[10px] text-slate-500 italic shrink-0">{c.title}</span>}
                      {c.department && (
                        <Badge className="bg-blue-50 text-blue-700 text-[10px] shrink-0">{c.department}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Inline add new contact */}
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3" data-testid="hqe-inline-add-contact">
                <div className="flex items-center gap-2 mb-2">
                  <UserPlus className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-xs font-semibold text-blue-900">Add a department contact</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Input
                    placeholder="Name (optional)"
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    data-testid="hqe-add-contact-name"
                  />
                  <Input
                    type="email"
                    placeholder="email@hospital.org *"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    data-testid="hqe-add-contact-email"
                  />
                  <Input
                    placeholder="Title (e.g. Director)"
                    value={newContact.title}
                    onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                    data-testid="hqe-add-contact-title"
                  />
                  <Input
                    placeholder="Phone"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  />
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Department</Label>
                    <Select
                      value={newContact.department}
                      onValueChange={(v) => setNewContact({ ...newContact, department: v })}
                    >
                      <SelectTrigger className="h-9" data-testid="hqe-add-contact-department"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={addContact}
                    disabled={addingContact || !newContact.email}
                    className="bg-blue-600 hover:bg-blue-700 gap-1.5"
                    data-testid="hqe-add-contact-btn"
                  >
                    {addingContact ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                    Add
                  </Button>
                </div>
              </div>
            </section>

            {/* Billing */}
            <section className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Billing address</h4>
              <Input
                placeholder="Street address"
                value={form.billing_address}
                onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  placeholder="City"
                  value={form.billing_city}
                  onChange={(e) => setForm({ ...form, billing_city: e.target.value })}
                />
                <Input
                  placeholder="State"
                  value={form.billing_state}
                  onChange={(e) => setForm({ ...form, billing_state: e.target.value })}
                />
                <Input
                  placeholder="ZIP"
                  value={form.billing_zip}
                  onChange={(e) => setForm({ ...form, billing_zip: e.target.value })}
                />
              </div>
            </section>

            <section>
              <Label className="text-xs">Notes</Label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </section>
          </div>
        )}

        <DialogFooter className="border-t pt-3 mt-3 flex justify-between sm:justify-between">
          <Button
            variant="ghost"
            onClick={openFullPage}
            disabled={!hospital?.id || saving}
            className="text-blue-700 hover:bg-blue-50 gap-1.5"
            data-testid="hqe-view-full-page"
          >
            <ExternalLink className="w-4 h-4" />
            View full hospital page
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              <X className="w-4 h-4 mr-1.5" /> Close
            </Button>
            <Button
              onClick={save}
              disabled={saving || !hospital?.id}
              className="bg-blue-600 hover:bg-blue-700 gap-1.5"
              data-testid="hqe-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
